import {
  BizNestClientError,
  EhttpController, FrontendLogger, IehttpControllerConfig
} from '@biz-nest/core-client';

/**
 * A client for interacting with the notification service.
 * This class provides HTTP client initialization and management for notification-related operations.
 *
 * @class NotificationClient
 * @example
 * // Initialize the notification client
 * const notificationClient = new NotificationClient({
 *   baseURL: 'https://api.example.com/notifications',
 *   timeout: 10000
 * });
 *
 * // Use the HTTP client
 * const response = await NotificationClient.ehttp.get('/some-endpoint');
 */
export class NotificationClient {
  /** Singleton instance of the HTTP controller */
  private static http: EhttpController;

  /**
   * Creates an instance of NotificationClient with comprehensive environment validation.
   * @param {IehttpControllerConfig} config - Configuration object for the Axios HTTP client
   * @throws {BizNestClientError} If the provided configuration is invalid
   * @example
   * const client = new NotificationClient({
   *   baseURL: 'https://api.example.com',
   *   timeout: 5000
   * });
   */
  constructor(config: IehttpControllerConfig) {
    if (!config || typeof config !== 'object') {
      throw new BizNestClientError('config must be a valid object');
    }

    FrontendLogger.debug('NotificationClient initialized');
    NotificationClient.http = new EhttpController(config);
  }

  /**
   * Gets the singleton instance of the HTTP controller.
   * @static
   * @returns {EhttpController} The HTTP controller instance
   * @throws {BizNestClientError} If the client has not been initialized
   * @example
   * // After initializing NotificationClient
   * const httpClient = NotificationClient.ehttp;
   * const response = await httpClient.get('/notifications');
   */
  static get ehttp(): EhttpController {
    if (!NotificationClient.http) {
      throw new BizNestClientError(`HTTP client not initialized. 
        Call new NotificationClient() with proper configuration first.`);
    }

    return NotificationClient.http;
  }
}
